import type { ConsentSendForm, ConsentStatus } from '@lmc/common/types'
import type { Resident } from '@lmc/ts-common'
import { showToast } from 'common/utils'
import dayjs from 'dayjs'
import { postJSON } from '../../../../../common/services/api'

type FormState = {
  ability: boolean
  capacity: boolean
  name: string
  relationship: string
  imageURL: string
  hasConfirmed: boolean
  lpaValue: any
  comment: string
  reason: string
}

type onSubmitType = {
  resident: Resident
  nextReviewDate: any
  consents?: ConsentSendForm[]
  formState: FormState
  consentSingleForm?: ConsentSendForm
  isSingleSign?: boolean
  item?: string
  category?: string
  status?: ConsentStatus
}

export const onSubmit = async ({
  resident,
  nextReviewDate,
  consents,
  formState,
  consentSingleForm,
  isSingleSign,
  item,
  category,
  status
}: onSubmitType) => {
  try {
    const {
      imageURL,
      capacity,
      ability,
      name: proxyName,
      relationship,
      reason,
      comment
    } = formState

    const signatureLogData = {
      status: status ?? null,
      capacity,
      ability,
      proxyName,
      relationship,
      reason: reason || undefined,
      comment: comment || undefined,
      item,
      category,
      consentFormTitle: consentSingleForm?.title
    }

    if (!isSingleSign) {
      const result = await postJSON({
        url: 'list/consent-forms/sign',
        json: {
          resident: resident.id,
          nextReviewDate:
            nextReviewDate && dayjs(nextReviewDate, 'DD-MM-YYYY').toISOString(),
          // @ts-ignore
          consents: consents.map((consentForm) => ({
            ...consentForm,
            signatureLogData: {
              ...signatureLogData,
              consentFormTitle: consentForm.title
            }
          }))
        }
      })

      const logIds: (string | null)[] = result.result?.logIds ?? []
      await Promise.all(
        logIds.map(async (logId) => {
          if (!logId) return
          const form = new FormData()
          // @ts-ignore
          form.append('picture1', dataURItoBlob(imageURL))
          try {
            await postJSON({
              url: `list/logs/${logId}`,
              method: 'PUT',
              contentType: 'multipart/form-data',
              body: form
            })
          } catch {
            // image upload is best-effort; consent forms are already created
          }
        })
      )

      return result
    }

    const signResult = await postJSON({
      url: `list/consent-forms/${consentSingleForm?.id}/sign`,
      json: { signatureLogData }
    })

    const logId = signResult.result?.signature?.id
    if (logId) {
      const form = new FormData()
      // @ts-ignore
      form.append('picture1', dataURItoBlob(imageURL))
      try {
        await postJSON({
          url: `list/logs/${logId}`,
          method: 'PUT',
          contentType: 'multipart/form-data',
          body: form
        })
      } catch {
        // image upload is best-effort; consent form is already signed
      }
    }

    return signResult
  } catch (e) {
    const title = e instanceof Error ? e.message : 'Oops! Something went wrong!'
    showToast({
      title,
      type: 'error'
    } as any)

    throw e
  }
}

export function dataURItoBlob(dataURI: any) {
  try {
    // convert base64/URLEncoded data component to raw binary data held in a string
    let byteString
    if (dataURI.split(',')[0].indexOf('base64') >= 0)
      byteString = atob(dataURI.split(',')[1])
    else byteString = unescape(dataURI.split(',')[1])

    // separate out the mime component
    // const mimeString = dataURI.split(',')[0].split(':')[1].split('')[0]

    // write the bytes of the string to a typed array
    const ia = new Uint8Array(byteString.length)
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i)
    }
    // const file = new Blob([ia], { type: mimeString })
    // const uri = URL.createObjectURL(file)
    // return {
    //     uri,
    //     type: 'image/png',
    //     name: `signature.png`
    // }
    return new Blob([ia], { type: 'image/png' })
  } catch (e) {
    return null
  }
}

export const shortCutBuilder = (input: string) => {
  const titleStringArray = input?.split(' ')
  const singleWord = titleStringArray?.length === 1
  if (singleWord) {
    return input?.toUpperCase()?.charAt(0)
  }

  const lastWordOfArray = titleStringArray?.slice(-1)[0]

  const secondWord = titleStringArray?.slice(
    lastWordOfArray === '' ? -2 : -1
  )[0]

  const shortCutBuilder = `${titleStringArray
    ?.shift()
    ?.charAt(0)
    ?.toUpperCase()}${secondWord
    ?.charAt(secondWord.startsWith('(') ? 1 : 0)
    ?.toUpperCase()}`
  return shortCutBuilder
}

export const setNextReviewDateByOptions = (
  timeOptionNextReview: string,
  setNextReviewDate: any,
  form: any
) => {
  if (timeOptionNextReview === '3 months') {
    const date = dayjs().add(3, 'months').endOf('day')
    setNextReviewDate(date)
    form.setFieldsValue({
      'date-picker': date
    })
  } else if (timeOptionNextReview === '6 months') {
    const date = dayjs().add(6, 'months').endOf('day')
    setNextReviewDate(date)
    form.setFieldsValue({
      'date-picker': date
    })
  } else if (timeOptionNextReview === '1 year') {
    const date = dayjs().add(1, 'years').endOf('day')
    setNextReviewDate(date)
    form.setFieldsValue({
      'date-picker': date
    })
  }
}

export const getTimeOptions = (translate: any) => [
  {
    label: translate('threeMonth'),
    value: '3 months'
  },
  {
    label: translate('sixMonths'),
    value: '6 months'
  },
  {
    label: translate('oneYear'),
    value: '1 year'
  }
]
